import React, { useEffect, useState } from 'react';
import { BrowserRouter, Link, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { ChartBar, CreditCard, Gift, House, Plus, Scroll, SignOut, UsersThree } from '@phosphor-icons/react';

import { cckCreditsApi } from './services/api';
import { CckCreditSummary } from './types';
import { PostSignupCreditsPopup, RedeemField } from './components/CckCredits';

import { AppProvider } from './context/AppContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Home from './pages/Home';
import Upload from './pages/Upload';
import ColumnPreview from './pages/ColumnPreview';
import Processing from './pages/Processing';
import History from './pages/History';
import Dashboard from './pages/Dashboard';
import JobDetail from './pages/JobDetail';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import Admin from './pages/Admin';
import Pricing from './pages/Pricing';
import Subscription from './pages/Subscription';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';

import './App.css';

const NOISE_SVG = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`;
const HALFTONE_SVG = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='8'%3E%3Ccircle cx='4' cy='4' r='1.4' fill='rgba(0,0,0,0.2)'/%3E%3C/svg%3E")`;

const TextureOverlay: React.FC = () => (
  <div aria-hidden="true" style={{ pointerEvents: 'none' }}>
    <div style={{ position: 'fixed', inset: 0, zIndex: 90, backgroundImage: NOISE_SVG, backgroundSize: '200px 200px', opacity: 0.12 }} />
    <div style={{ position: 'fixed', inset: 0, zIndex: 91, backgroundImage: HALFTONE_SVG, backgroundSize: '8px 8px', opacity: 0.3 }} />
    <div style={{ position: 'fixed', inset: 0, zIndex: 99, background: 'radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.6) 100%)' }} />
  </div>
);

// Static credits indicator — lives in the shared sidebar footer so its position
// never changes between pages (UX rule). When the CCK credit system is live it
// shows "N credits" (amber when expiring soon); when it's dark it falls back to
// the Stripe dial indicator ("Unlimited" / "N left") — exact pre-feature behaviour.
// Inline modal to claim free credits with a CCK code (no page change).
const ClaimCreditsModal: React.FC<{ onClose: () => void; onClaimed: () => void }> = ({ onClose, onClaimed }) => {
  const [done, setDone] = useState<number | null>(null);
  return (
    <div
      role="dialog" aria-modal="true" aria-label="Claim free credits" onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
    >
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: '#100a05', border: '1px solid var(--amber)', borderRadius: 10, padding: 24, width: 360, maxWidth: '100%', boxShadow: '0 0 30px rgba(245,166,35,0.25)' }}>
        <h3 style={{ margin: '0 0 6px', color: 'var(--amber)', fontFamily: 'var(--font-display)', letterSpacing: '0.04em', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Gift size={20} weight="fill" /> Claim Free Credits
        </h3>
        <p style={{ margin: '0 0 14px', color: 'var(--text-soft)', fontSize: 13 }}>
          Enter your CCK code for free trial credits — no card needed. 1 credit = 1 recording.
        </p>
        {done != null ? (
          <div style={{ color: 'var(--category-good-fg)', fontSize: 14, marginBottom: 12 }}>
            🎉 You now have <strong>{done.toLocaleString()}</strong> credits.
          </div>
        ) : (
          <RedeemField autoFocus onSuccess={(r) => { setDone(r.balance); onClaimed(); }} />
        )}
        <div style={{ marginTop: 14, textAlign: 'right' }}>
          <button onClick={onClose}
            style={{ background: 'none', border: '1px solid var(--border)', color: 'var(--text-faint)', padding: '6px 12px', cursor: 'pointer', fontFamily: 'var(--font-data)', fontSize: 12 }}>
            {done != null ? 'Done' : 'Close'}
          </button>
        </div>
      </div>
    </div>
  );
};

const smallLine: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none',
  padding: '8px 16px', borderTop: '1px solid var(--navy-700)',
};

const CreditsIndicator: React.FC = () => {
  const [sm, setSm] = useState<CckCreditSummary | null>(null);
  const [failed, setFailed] = useState(false);
  const [claimOpen, setClaimOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let alive = true;
    cckCreditsApi.getSummary()
      .then((s) => { if (alive) setSm(s); })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [reloadKey]);

  if (failed || !sm) return null;

  // Not metered (admin / exempt / both features off) → unobtrusive line.
  if (!sm.metered) {
    return (
      <Link to="/subscription" className="sidebar-credits" title="Credits & subscription"
        style={{ ...smallLine, color: 'var(--text-soft)' }}>
        <CreditCard size={14} weight="duotone" style={{ color: 'var(--amber)', flexShrink: 0 }} />
        <span style={{ fontFamily: 'var(--font-data)', fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          Unlimited
        </span>
      </Link>
    );
  }

  const total = sm.total_remaining;
  const trial = sm.cck.enabled ? sm.cck.balance : 0;
  const plan = sm.stripe?.spendable ? (sm.stripe.remaining ?? 0) : 0;
  const warn = sm.cck.expiring_soon;

  // Out of combined credits → claim banner (if still claimable) or upgrade prompt.
  if (total <= 0) {
    if (sm.cck.can_redeem) {
      return (
        <>
          <button className="claim-credits-banner" onClick={() => setClaimOpen(true)}>
            <Gift size={22} weight="fill" className="claim-credits-icon" />
            <span className="claim-credits-text">
              <span className="claim-credits-title">Claim Free Credits</span>
              <span className="claim-credits-sub">Redeem your code → free recordings</span>
            </span>
          </button>
          {claimOpen && (
            <ClaimCreditsModal onClose={() => setClaimOpen(false)} onClaimed={() => setReloadKey((k) => k + 1)} />
          )}
        </>
      );
    }
    return (
      <Link to="/pricing" className="sidebar-credits" title="Out of credits"
        style={{ ...smallLine, color: 'var(--amber)' }}>
        <CreditCard size={14} weight="duotone" style={{ flexShrink: 0 }} />
        <span style={{ fontFamily: 'var(--font-data)', fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          Out of credits — upgrade
        </span>
      </Link>
    );
  }

  // Combined balance available → one card, with the trial + plan breakdown.
  const parts: string[] = [];
  if (trial > 0) parts.push(`${trial.toLocaleString()} trial`);
  if (plan > 0) parts.push(`${plan.toLocaleString()} plan`);
  const subLine = parts.length === 2 ? parts.join(' + ') : (warn ? 'expiring soon' : 'available');

  return (
    <Link to="/subscription" className="sidebar-credits-card" title="Credits & subscription">
      <CreditCard size={18} weight="duotone" className="sidebar-credits-card-icon" />
      <span className="sidebar-credits-card-text">
        <span className="sidebar-credits-card-balance" style={{ color: warn ? 'var(--amber)' : 'var(--text-strong)' }}>
          {total.toLocaleString()} credits
        </span>
        <span className="sidebar-credits-card-sub" style={{ color: warn ? 'var(--amber)' : 'var(--text-faint)' }}>
          {subLine}
        </span>
      </span>
    </Link>
  );
};

const Sidebar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const p = location.pathname;
  const isInWorkflow = p === '/upload' || p === '/column-preview';

  return (
    <aside className="app-sidebar">
      <Link to="/" className="sidebar-logo">
        <div className="sidebar-logo-icon">
          <img className="sidebar-logo-icon-img" src={`${process.env.PUBLIC_URL}/icon.png`} alt="Dial Sniper" />
        </div>
        <div className="sidebar-logo-text">
          <span className="sidebar-logo-title">Dial Sniper</span>
          <span className="sidebar-logo-sub">Command Center</span>
        </div>
      </Link>

      <nav className="sidebar-nav">
        <Link to="/" className={`sidebar-nav-item${p === '/' ? ' active' : ''}`}>
          <House size={16} weight="duotone" /> Overview
        </Link>
        <Link to="/dashboard" className={`sidebar-nav-item${p === '/dashboard' ? ' active' : ''}`}>
          <ChartBar size={16} weight="duotone" /> Analytics
        </Link>
        <Link to="/history" className={`sidebar-nav-item${p.startsWith('/history') ? ' active' : ''}`}>
          <Scroll size={16} weight="duotone" /> Runs
        </Link>
        <Link to="/subscription" className={`sidebar-nav-item${p.startsWith('/subscription') ? ' active' : ''}`}>
          <CreditCard size={16} weight="duotone" /> Billing
        </Link>
        {user?.role === 'admin' && (
          <Link to="/admin" className={`sidebar-nav-item${p.startsWith('/admin') ? ' active' : ''}`}>
            <UsersThree size={16} weight="duotone" /> Users
          </Link>
        )}

        {isInWorkflow && (
          <>
            <div className="sidebar-section-label" style={{ marginTop: 12 }}>New Run</div>
            <Link to="/upload" className={`sidebar-nav-item sidebar-workflow-item${p === '/upload' ? ' active' : ''}`}>
              Upload File
            </Link>
            <Link to="/column-preview" className={`sidebar-nav-item sidebar-workflow-item${p === '/column-preview' ? ' active' : ''}`}>
              Select Columns
            </Link>
          </>
        )}
      </nav>

      <div
        style={{
          padding: '10px 16px',
          borderTop: '1px solid var(--navy-700)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        <div style={{ overflow: 'hidden' }}>
          <div style={{ fontFamily: 'var(--font-data)', fontSize: 12, color: 'var(--amber)', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            {user?.username}
          </div>
          <div style={{ fontFamily: 'var(--font-data)', fontSize: 10, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            {user?.role}
          </div>
        </div>
        <button
          onClick={() => logout().then(() => navigate('/login'))}
          title="Sign out"
          style={{ background: 'none', border: '1px solid var(--border)', color: 'var(--text-faint)', padding: '5px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', flexShrink: 0 }}
        >
          <SignOut size={14} />
        </button>
      </div>

      <CreditsIndicator />

      <div className="sidebar-bottom">
        <button className="sidebar-new-run-btn" onClick={() => navigate('/upload')}>
          <Plus size={13} weight="bold" /> New Run
        </button>
      </div>

      <div className="sidebar-footer">
        Dial Sniper v2<br />
        Dialing + AI Classification
      </div>
    </aside>
  );
};

const ProtectedShell: React.FC = () => {
  const { user } = useAuth();
  return (
    <ProtectedRoute>
      <div className="app">
        <TextureOverlay />
        <PostSignupCreditsPopup />
        <Sidebar />
        <div className="app-body">
          <main className="app-main">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/upload" element={<Upload />} />
              <Route path="/column-preview" element={<ColumnPreview />} />
              <Route path="/processing/:jobId" element={<Processing />} />
              <Route path="/history" element={<History />} />
              <Route path="/history/:jobId" element={<JobDetail />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/subscription" element={<Subscription />} />
              <Route
                path="/admin"
                element={user?.role === 'admin' ? <Admin /> : <Navigate to="/" replace />}
              />
            </Routes>
          </main>
        </div>
      </div>
    </ProtectedRoute>
  );
};

function AppShell() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      {/* Public legal pages — must be reachable logged-out (signup links, Google/Stripe). */}
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      {/* Public pricing — works logged-out (→ signup) and logged-in (→ checkout). */}
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/*" element={<ProtectedShell />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppProvider>
        <BrowserRouter>
          <AppShell />
        </BrowserRouter>
      </AppProvider>
    </AuthProvider>
  );
}

export default App;
